import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import type { AttributeCode } from '@vs-shop/schemas/product/filter/state/attributeCode';
import type { TextFilterStateMap } from '@vs-shop/schemas/product/filter/state/text';
import { Context, Effect, HashMap, HashSet, Layer, Option, Stream, SubscriptionRef } from 'effect';

import { ContextTag } from '../context';

export type ContextualTextFilterStateMap = HashMap.HashMap<ProductContextId, TextFilterStateMap>;

// ===== EXPORTED UTILITIES =====
// Standalone stream utilities for atom usage

export const createTextFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTextFilterStateMap>,
  context: ProductContextId
) => {
  const emptyFilterMap = HashMap.empty<AttributeCode, HashSet.HashSet<string>>();
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyFilterMap)),
    Stream.changes
  );
};

export const createTextFilterByCodeStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTextFilterStateMap>,
  context: ProductContextId,
  attributeCode: AttributeCode
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.flatMap((filters) => HashMap.get(filters, attributeCode)))
  );

export const createTextHasValueStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTextFilterStateMap>,
  context: ProductContextId,
  attributeCode: AttributeCode,
  value: string
) =>
  createTextFilterByCodeStream(ref, context, attributeCode).pipe(
    Stream.map((option) =>
      Option.match(option, {
        onNone: () => false,
        onSome: (hashSet) => HashSet.has(hashSet, value),
      })
    )
  );

export const createTextHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTextFilterStateMap>,
  context: ProductContextId
) => createTextFiltersStream(ref, context).pipe(Stream.map((filters) => HashMap.size(filters) > 0));

export class TextFilterService extends Context.Service<TextFilterService>()(
  ContextTag.TextFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, TextFilterStateMap>();
      const emptyFilterMap = HashMap.empty<AttributeCode, HashSet.HashSet<string>>();
      const emptyStringSet = HashSet.empty<string>();

      const filtersRef = yield* SubscriptionRef.make<ContextualTextFilterStateMap>(emptyContextMap);

      const getFilters = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyFilterMap));
        });

      const getFilterByCode = (context: ProductContextId, attributeCode: AttributeCode) =>
        Effect.gen(function* () {
          const contextFilters = yield* getFilters(context);
          return HashMap.get(contextFilters, attributeCode);
        });

      const addValue = (
        context: ProductContextId,
        attributeCode: AttributeCode,
        newValue: string
      ) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const currentValue = HashMap.get(currentContextFilters, attributeCode).pipe(
            Option.getOrElse(() => emptyStringSet)
          );
          const updatedValue = HashSet.add(currentValue, newValue);
          const updatedContextFilters = HashMap.set(
            currentContextFilters,
            attributeCode,
            updatedValue
          );
          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const removeValue = (
        context: ProductContextId,
        attributeCode: AttributeCode,
        valueToRemove: string
      ) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const currentValue = HashMap.get(currentContextFilters, attributeCode).pipe(
            Option.getOrElse(() => emptyStringSet)
          );
          const updatedValue = HashSet.remove(currentValue, valueToRemove);

          if (HashSet.size(updatedValue) === 0) {
            const updatedContextFilters = HashMap.remove(currentContextFilters, attributeCode);
            if (HashMap.isEmpty(updatedContextFilters)) {
              return HashMap.remove(contextMap, context);
            }
            return HashMap.set(contextMap, context, updatedContextFilters);
          }

          const updatedContextFilters = HashMap.set(
            currentContextFilters,
            attributeCode,
            updatedValue
          );
          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const removeFilter = (context: ProductContextId, attributeCode: AttributeCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const updatedContextFilters = HashMap.remove(currentContextFilters, attributeCode);

          if (HashMap.isEmpty(updatedContextFilters)) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const clearFilters = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setAllFilters = (context: ProductContextId, filters: TextFilterStateMap) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, filters));

      // ===== STREAMING METHODS =====
      // Backward compatible wrappers using exported utilities

      const getFiltersStream = (context: ProductContextId) =>
        createTextFiltersStream(filtersRef, context);

      const getFilterByCodeStream = (context: ProductContextId, attributeCode: AttributeCode) =>
        createTextFilterByCodeStream(filtersRef, context, attributeCode);

      const hasValueStream = (
        context: ProductContextId,
        attributeCode: AttributeCode,
        value: string
      ) => createTextHasValueStream(filtersRef, context, attributeCode, value);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createTextHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        // Effect-based methods (existing)
        getFilters,
        getFilterByCode,
        addValue,
        removeValue,
        removeFilter,
        clearFilters,
        setAllFilters,
        // Stream-based methods (new)
        getFiltersStream,
        getFilterByCodeStream,
        hasValueStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
